import {
    connect as socketConnect,
    disconnect as socketDisconnect,
    handleMessage,
    send as socketSend
} from './socketConnection';
import { log } from 'log';
import { dispatcher } from 'dispatcher';
import pushDispatcher from './pushDispatcher';
import { storageService } from 'js/services/storage.service';

let openApiConnection;
let forceReconnectNeeded = false;
const reconnectionAttempts = 3;

const onConnect = () => {
    log('log', 'Open API connected');

    dispatcher.dispatch('server:socket:connect');

    const userDisconnected = storageService.session.getItem('userDisconnected');

    if (userDisconnected) {
        restoreSession();
    }
};

const onError = () => {
    log('log', 'Open API connection error');
    dispatcher.dispatch('server:error');
    dispatcher.dispatch('server:reconnect');
};

const onConnectFailed = () => {
    log('log', 'Open API connection failed');
    dispatcher.dispatch('server:connect_failed');
};

const onDisconnect = () => {
    log('log', 'Open API disconnected');
    dispatcher.dispatch('server:socket:disconnect');
    storageService.session.setItem('userDisconnected', true);
};

const onReconnectFailed = () => {
    log('log', 'Open API reconnect failed');
};

const onReconnecting = (reconnectionDelay, attemptNumber) => {
    log('log', `reconnecting: ${attemptNumber}`);

    if (attemptNumber === reconnectionAttempts) {
        dispatcher.dispatch('session:reconnecting:notification');
    }
};

const onMessage = (message) => {
    const { nextConnection, request, response, error, pushMessage, time } = handleMessage(openApiConnection, message);
    openApiConnection = nextConnection;

    if (pushMessage) {
        return handlePush(pushMessage);
    }

    if (!error) {
        const { description } = request;
        log('oapiResponse', { ...response, description, time });
    }

    dispatcher.dispatch('server:message', response);

    handleForceReconnect(request);
};

const handlePush = (pushMessage) => {
    log('oapiPush', pushMessage);

    dispatcher.dispatch('server:message', pushMessage);

    const { ID } = pushMessage;
    pushDispatcher.trigger(ID, pushMessage);
};

const restoreSession = () => {
    const userObject = storageService.session.getItem('userModel');

    if (userObject?.role === 'user') {
        dispatcher.dispatch('session:restore');

        dispatcher.once(
            ['auth:login', 'auth:logout'],
            () => storageService.session.removeItem('userDisconnected')
        );
    }
};

const handleForceReconnect = (request) => {
    if (request.ID === 300) {
        forceReconnectNeeded = true;
    }

    if (forceReconnectNeeded && openApiConnection.stack.length === 0) {
        forceReconnectNeeded = false;
        disconnect();
        connect({ ...openApiConnection });
    }
};

const addListeners = () => {
    openApiConnection.io.on('connect', onConnect);
    openApiConnection.io.on('error', onError);
    openApiConnection.io.on('connect_failed', onConnectFailed);
    openApiConnection.io.on('disconnect', onDisconnect);
    openApiConnection.io.on('reconnect_failed', onReconnectFailed);
    openApiConnection.io.on('reconnecting', onReconnecting);

    openApiConnection.io.on('message', onMessage);
};

const removeListeners = () => openApiConnection.io.removeAllListeners();

export const connect = ({ url, options }) => {
    log('log', `Trying to connect to Open API: ${url}`);
    openApiConnection = socketConnect(url, options);
    addListeners();
};

export const disconnect = () => {
    if (openApiConnection) {
        openApiConnection = socketDisconnect(openApiConnection);
        removeListeners();
    }
};

export const send = (request) => {
    if (!openApiConnection || openApiConnection.io.socket.connected === false) {
        // either initial connection or socket is disconnected
        const reconnectPromise = new Promise((resolve, reject) => {
            dispatcher.once(['server:socket:connect'], resolve);
            dispatcher.once(['server:error'], reject);
        })
            .then(() => send(request));

        return patchPromise(reconnectPromise);
    }

    log('oapiRequest', request);

    const { nextConnection, promise } = socketSend(openApiConnection, request);
    openApiConnection = nextConnection;

    const catchedPromise = promise.catch((error) => {
        const { stackRequest, ...nextError } = error;

        if (stackRequest) {
            // remove request from stack in case of timeout
            openApiConnection = {
                ...openApiConnection,
                stack: openApiConnection.stack.filter(r => r !== stackRequest)
            };

            nextError.request = stackRequest.request;
        }

        log('oapiError', nextError);

        throw nextError;
    });

    /**
     * Emulate legacy behaviour for success in request.
     */
    catchedPromise
        .then((response) => {
            if (request.success) {
                request.success(response);
            }
        })
        .catch((error) => {
            const legacyError = error.response ?? error.request ?? error;

            if (request.error) {
                request.error(legacyError);
            }
        });

    return patchPromise(catchedPromise);
};

export const getConnection = () => openApiConnection;

/**
 * Patch promise to work in Backbone.
 * Should be removed once all .done and .fail
 * will be removed along with $.Deferred();
 */
export const patchPromiseState = (promise) => {
    let state = 'pending';

    if (promise.state) {
        return promise;
    }

    const patchPromise = promise.then((response) => {
        state = 'resolved';

        return response;
    }, (error) => {
        state = 'rejected';

        throw error;
    });

    patchPromise.state = () => state;

    return patchPromise;
};

export const patchPromise = (originPromise) => {
    const promise = patchPromiseState(originPromise);
    const originThen = promise.then.bind(promise);

    promise.then = function(doneCallback, failCallback) {
        return patchPromise(originThen(doneCallback, failCallback));
    };
    promise.done = function(callback) {
        return patchPromise(promise.then((response) => {
            // must not return anything
            callback(response);

            return response;
        }));
    };
    promise.fail = function(callback) {
        return patchPromise(promise.catch((error) => {
            const legacyError = error.response ?? error.request ?? error;

            // must not return anything
            callback(legacyError);

            throw legacyError;
        }));
    };
    promise.always = function(callback) {
        let result;

        return patchPromise(promise
            .then(response => result = response)
            .catch(error => {
                result = error.response ?? error.request ?? error;

                throw result;
            })
            .finally(() => callback(result))
        );
    };
    promise.promise = function() {
        return promise;
    };

    return promise;
};
